import React, { useState, useEffect, useContext } from 'react';
import { Pin, Eye, EyeOff, Star, StarOff, Trash2, Plus, RefreshCw } from 'lucide-react';
import { AuthContext } from '../AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const font = {
  mono:      '"JetBrains Mono", monospace',
  condensed: '"Barlow Condensed", sans-serif',
  body:      '"Barlow", sans-serif',
};

const S = {
  page:      { padding: '2.5rem', maxWidth: 1100 },
  section:   { background: '#0a0a0a', border: '1px solid #111', padding: '1.5rem', marginBottom: '1.25rem' },
  secTitle:  { fontFamily: font.mono, fontSize: '0.58rem', color: '#444', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1.25rem' },
  label:     { fontFamily: font.mono, fontSize: '0.55rem', color: '#444', letterSpacing: '0.12em', marginBottom: 5, display: 'block', textTransform: 'uppercase' },
  input:     { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.mono, fontSize: '0.7rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
  btnSave:   { background: '#fff', color: '#000', border: 'none', padding: '0.65rem 1.75rem', fontFamily: font.mono, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost:  { background: 'none', border: '1px solid #1a1a1a', color: '#666', padding: '0.3rem 0.6rem', fontFamily: font.mono, fontSize: '0.58rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
  btnDanger: { background: 'none', border: '1px solid #2a1010', color: '#ff6b6b', padding: '0.3rem 0.6rem', fontFamily: font.mono, fontSize: '0.58rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
  tag:       { fontFamily: font.mono, fontSize: '0.48rem', letterSpacing: '0.1em', padding: '2px 6px', border: '1px solid', borderRadius: 2 },
};

const Badge = ({ label, color }) => (
  <span style={{ ...S.tag, color, borderColor: color }}>
    {label}
  </span>
);

const ClipRow = ({ clip, onUpdate, onDelete }) => {
  const thumb = clip.thumbnail_url;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '80px 1fr auto',
      gap: '1rem', alignItems: 'center',
      padding: '0.85rem 0',
      borderBottom: '1px solid #0d0d0d',
      opacity: clip.is_hidden ? 0.45 : 1,
    }}>
      {/* Thumbnail */}
      <div style={{ width: 80, height: 45, background: '#0a0a0a', flexShrink: 0, overflow: 'hidden' }}>
        {thumb
          ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#222', fontFamily: font.mono, fontSize: '0.45rem' }}>NO IMG</span>
            </div>
        }
      </div>

      {/* Info */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1rem', color: '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
          {clip.title || clip.clip_id}
        </div>
        <div style={{ fontFamily: font.mono, fontSize: '0.58rem', color: '#555', marginBottom: 6 }}>
          {[clip.broadcaster_name || clip.broadcaster_login, clip.game_name, clip.clip_id].filter(Boolean).join(' · ')}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {clip.is_pinned   && <Badge label="PINNED"   color="#FFD700" />}
          {clip.is_featured && <Badge label="FEATURED" color="#4ade80" />}
          {clip.is_hidden   && <Badge label="HIDDEN"   color="#ff4444" />}
          <span style={{ fontFamily: font.mono, fontSize: '0.5rem', color: '#333' }}>
            {clip.like_count || 0} likes · {clip.view_count ? `${clip.view_count.toLocaleString()} views` : ''}
            {clip.created_at ? ` · ${new Date(clip.created_at).toLocaleDateString()}` : ''}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <button
          style={{ ...S.btnGhost, color: clip.is_featured ? '#4ade80' : '#555', borderColor: clip.is_featured ? '#1a3a1a' : '#1a1a1a' }}
          onClick={() => onUpdate(clip.id, { is_featured: !clip.is_featured })}
          title={clip.is_featured ? 'Unfeature' : 'Feature in homepage carousel'}
        >
          {clip.is_featured ? <Star size={11} fill="#4ade80" stroke="#4ade80" /> : <StarOff size={11} />}
          {clip.is_featured ? 'FEATURED' : 'FEATURE'}
        </button>

        <button
          style={{ ...S.btnGhost, color: clip.is_pinned ? '#FFD700' : '#555', borderColor: clip.is_pinned ? '#333200' : '#1a1a1a' }}
          onClick={() => onUpdate(clip.id, { is_pinned: !clip.is_pinned })}
          title={clip.is_pinned ? 'Unpin' : 'Pin to top of carousel'}
        >
          <Pin size={11} />
          {clip.is_pinned ? 'PINNED' : 'PIN'}
        </button>

        <button
          style={{ ...S.btnGhost, color: clip.is_hidden ? '#aaa' : '#555' }}
          onClick={() => onUpdate(clip.id, { is_hidden: !clip.is_hidden })}
          title={clip.is_hidden ? 'Show clip' : 'Hide clip'}
        >
          {clip.is_hidden ? <Eye size={11} /> : <EyeOff size={11} />}
          {clip.is_hidden ? 'SHOW' : 'HIDE'}
        </button>

        <button
          style={S.btnDanger}
          onClick={() => onDelete(clip.id, clip.title || clip.clip_id)}
          title="Permanently delete"
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
};

const AdminHighlights = () => {
  const { authToken } = useContext(AuthContext);
  const [clips,   setClips]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState('');
  const [clipId,  setClipId]  = useState('');
  const [isFeatured, setIsFeatured] = useState(true);
  const [isPinned,   setIsPinned]   = useState(false);
  const [filter, setFilter] = useState('all'); // all | featured | hidden

  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/highlights`, { headers })
      .then(r => r.json())
      .then(d => setClips(d.clips || []))
      .catch(() => showToast('Failed to load clips'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleUpdate = async (id, payload) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/highlights/${id}`, {
        method: 'PATCH', headers, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setClips(prev => prev.map(c => c.id === id ? { ...c, ...payload } : c));
      showToast('✓ Updated');
    } catch { showToast('Failed to update clip'); }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Permanently delete "${label}"? This cannot be undone.`)) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/highlights/${id}`, { method: 'DELETE', headers });
      setClips(prev => prev.filter(c => c.id !== id));
      showToast('✓ Deleted');
    } catch { showToast('Failed to delete'); }
  };

  const handleAdd = async () => {
    const id = clipId.trim()
      .replace('https://clips.twitch.tv/', '')
      .replace('https://www.twitch.tv/clip/', '')
      .split('?')[0].split('/').pop();

    if (!id || !/^[A-Za-z0-9_-]+$/.test(id)) {
      showToast('Enter a valid Twitch clip ID or URL');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/highlights`, {
        method: 'POST', headers,
        body: JSON.stringify({ clip_id: id, is_featured: isFeatured, is_pinned: isPinned }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.clip) setClips(prev => [data.clip, ...prev]);
      setClipId('');
      setIsFeatured(true);
      setIsPinned(false);
      showToast('✓ Clip added — metadata auto-fetched from Twitch');
    } catch { showToast('Failed to add clip'); }
    finally { setSaving(false); }
  };

  const displayClips = clips.filter(c => {
    if (filter === 'featured') return c.is_featured && !c.is_hidden;
    if (filter === 'hidden')   return c.is_hidden;
    return true;
  });

  return (
    <div style={S.page}>
      <style>{`@keyframes fadein{from{opacity:0}to{opacity:1}} input:focus{border-color:#333!important;} select:focus{outline:none;}`}</style>

      {toast && (
        <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', background: '#111', border: '1px solid #222', color: '#fff', fontFamily: font.mono, fontSize: '0.7rem', padding: '0.75rem 1.25rem', zIndex: 999, animation: 'fadein 0.2s ease', maxWidth: 360 }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ fontFamily: font.mono, fontSize: '0.58rem', color: '#333', letterSpacing: '0.2em', marginBottom: 6 }}>// MANAGE</div>
          <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '2.5rem', letterSpacing: '-0.02em' }}>Highlights</div>
          <p style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.82rem', color: '#444', marginTop: 4 }}>
            Add Twitch clips, feature them in the homepage carousel, pin one to appear first.
          </p>
        </div>
        <button style={{ ...S.btnGhost, gap: 6 }} onClick={load}><RefreshCw size={11} /> REFRESH</button>
      </div>

      {/* Add clip */}
      <div style={S.section}>
        <div style={S.secTitle}>// Add Clip</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
          <div>
            <label style={S.label}>Twitch Clip ID or URL</label>
            <input
              style={S.input}
              value={clipId}
              onChange={e => setClipId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="e.g. GleamingSpinelessSandpiperYouDontSay or https://clips.twitch.tv/..."
            />
          </div>
          <button style={S.btnSave} onClick={handleAdd} disabled={saving || !clipId.trim()}>
            <Plus size={13} />{saving ? 'ADDING...' : 'ADD CLIP'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: font.mono, fontSize: '0.6rem', color: '#666', cursor: 'pointer' }}>
            <input type="checkbox" checked={isFeatured} onChange={e => setIsFeatured(e.target.checked)} style={{ accentColor: '#fff' }} />
            FEATURE IN HOMEPAGE CAROUSEL
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: font.mono, fontSize: '0.6rem', color: '#666', cursor: 'pointer' }}>
            <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} style={{ accentColor: '#FFD700' }} />
            PIN TO TOP OF CAROUSEL
          </label>
        </div>
      </div>

      {/* Clips list */}
      <div style={S.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div style={S.secTitle}>// {clips.length} Clips Total</div>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ background: '#060606', border: '1px solid #1a1a1a', color: '#666', fontFamily: font.mono, fontSize: '0.58rem', padding: '0.3rem 0.6rem', outline: 'none', cursor: 'pointer', letterSpacing: '0.08em' }}
          >
            <option value="all">ALL CLIPS</option>
            <option value="featured">FEATURED ONLY</option>
            <option value="hidden">HIDDEN ONLY</option>
          </select>
        </div>

        {loading && (
          <div style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#333', padding: '1rem 0' }}>LOADING...</div>
        )}

        {!loading && displayClips.length === 0 && (
          <div style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#333', padding: '1rem 0' }}>
            {filter === 'all' ? 'No clips yet — add one above.' : `No clips match this filter.`}
          </div>
        )}

        {!loading && displayClips.map(clip => (
          <ClipRow key={clip.id} clip={clip} onUpdate={handleUpdate} onDelete={handleDelete} />
        ))}
      </div>

      {/* Legend */}
      <div style={{ fontFamily: font.mono, fontSize: '0.55rem', color: '#2a2a2a', lineHeight: 2 }}>
        <div>FEATURED → clip appears in the homepage highlights carousel</div>
        <div>PINNED → that clip always displays first in the carousel (only one clip can be pinned)</div>
        <div>HIDDEN → clip is invisible on site without being permanently deleted</div>
      </div>
    </div>
  );
};

export default AdminHighlights;
